import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Gauge, Plant, Calendar, Warning, Info, CheckCircle } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * SpoonFeedCalculator — pro-grade N application planner for the lawn-nerd
 * segment. Target rate 0.1-0.5 lb N / 1,000 sqft every 7-21 days.
 */
export const SpoonFeedCalculator = ({ defaultSqft = '' }) => {
  const [lawn, setLawn] = useState(defaultSqft || '');
  const [nPct, setNPct] = useState('24');
  const [rate, setRate] = useState('0.25');
  const [interval, setInterval] = useState('14');
  const [weeks, setWeeks] = useState('12');
  const [productName, setProductName] = useState('24-0-4 fertilizer');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/calculator/spoon-feed`, {
        lawn_sqft: parseFloat(lawn),
        n_percent: parseFloat(nPct),
        rate_lb_n_per_1000: parseFloat(rate),
        interval_days: parseInt(interval, 10),
        weeks_duration: parseInt(weeks, 10),
        product_name: productName,
        start_date: new Date().toISOString().slice(0, 10),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="spoon-feed-card">
      <CardContent className="p-5 sm:p-7 space-y-5">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
            <Gauge weight="fill" size={14} /> Pro-grade
          </div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mt-1">Spoon-Feed Planner</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Deliver a small, steady drip of nitrogen every 10–14 days instead of 2–3 heavy blasts. Consistent color, no surge growth, no burns.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Lawn (sqft)" value={lawn} onChange={setLawn} placeholder="5000" testId="sf-sqft" type="number" />
          <Field label="Fertilizer N %" value={nPct} onChange={setNPct} placeholder="24" testId="sf-npct" type="number" step="0.1" />
          <Field label="Target rate (lb N / 1,000)" value={rate} onChange={setRate} placeholder="0.25" testId="sf-rate" type="number" step="0.05" />
          <Field label="Interval (days)" value={interval} onChange={setInterval} placeholder="14" testId="sf-interval" type="number" />
          <Field label="Duration (weeks)" value={weeks} onChange={setWeeks} placeholder="12" testId="sf-weeks" type="number" />
          <Field label="Product name" value={productName} onChange={setProductName} placeholder="24-0-4 fertilizer" testId="sf-product" />
        </div>

        <Button onClick={submit} disabled={loading || !lawn || !nPct} className="w-full sm:w-auto" data-testid="sf-submit">
          {loading ? 'Calculating…' : 'Build my schedule'}
        </Button>

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive" data-testid="sf-error">
            <Warning size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="sf-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Per feeding" value={`${result.per_feeding.lbs_product} lbs`} sub={`${result.per_feeding.lbs_n_applied} lb N`} />
              <Stat label="Feedings" value={result.season_totals.num_feedings} sub={`over ${result.season_totals.weeks_covered} wks`} />
              <Stat label="Season total" value={`${result.season_totals.total_product_lbs} lbs`} sub={`${result.season_totals.total_n_lbs} lb N`} />
              <Stat label="Season N/1000" value={`${result.season_totals.total_n_lb_per_1000}`} sub="lb N / 1,000 sqft" />
            </div>

            {result.warnings?.length > 0 && (
              <div className="space-y-1.5">
                {result.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-700 dark:text-amber-400">
                    <Warning size={13} className="shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                <Calendar size={12} /> Application schedule
              </div>
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="grid grid-cols-[60px_1fr_1fr_1fr] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <div>#</div>
                  <div>Date</div>
                  <div className="text-right">Product</div>
                  <div className="text-right">N applied</div>
                </div>
                {result.schedule.map((s) => (
                  <div key={s.feeding_number} className="grid grid-cols-[60px_1fr_1fr_1fr] gap-2 px-3 py-2 border-t border-border/40 text-sm" data-testid={`sf-row-${s.feeding_number}`}>
                    <div className="font-mono text-muted-foreground">{s.feeding_number}</div>
                    <div>{s.date}</div>
                    <div className="text-right font-mono">{s.lbs_product} lbs</div>
                    <div className="text-right font-mono">{s.lbs_n_applied} lb N</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md bg-primary/5 border border-primary/20 p-3 text-xs">
              <Info size={13} className="shrink-0 mt-0.5 text-primary" />
              <span>{result.methodology_note}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Field = ({ label, value, onChange, placeholder, testId, type = 'text', step }) => (
  <div>
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1"
      data-testid={testId}
    />
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className="rounded border border-border/60 bg-card px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-lg font-bold font-mono mt-0.5">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

export default SpoonFeedCalculator;
